import { lazy, Suspense, useEffect, memo } from "react";
import { useShallow } from "zustand/react/shallow";
import { Loader2 } from "lucide-react";
import { useAppStore } from "@/stores/useAppStore";
// Moving HomeView to lazy to reduce main-thread script evaluation
const HomeView = lazy(() => import("@/components/views/HomeView"));

// Lazy-loaded views
const ProviderProfileView = lazy(() => import("@/components/views/ProviderProfileView"));
const ProfileView = lazy(() => import("@/components/views/ProfileView"));
const BookingsView = lazy(() => import("@/components/views/BookingsView"));
const ChatView = lazy(() => import("@/components/views/ChatView"));
const InboxView = lazy(() => import("@/components/views/InboxView"));
const BusinessOnboardingView = lazy(() => import("@/components/views/BusinessOnboardingView"));
const AdminVerificationView = lazy(() => import("@/components/views/AdminVerificationView"));
const AdminOperationsView = lazy(() => import("@/components/views/AdminOperationsView"));
const AllProvidersView = lazy(() => import("@/components/views/AllProvidersView"));
const ServiceResultsView = lazy(() => import("@/components/views/ServiceResultsView"));
const ProviderHubView = lazy(() => import("@/components/views/ProviderHubView"));
const ProviderJobsView = lazy(() => import("./views/ProviderJobsView"));
const ProviderJobHistoryView = lazy(() => import("@/components/views/ProviderJobHistoryView"));
const ProviderUpcomingJobsView = lazy(() => import("@/components/views/ProviderUpcomingJobsView"));
const ProviderInboxView = lazy(() => import("@/components/views/ProviderInboxView"));
const ProviderAIView = lazy(() => import("@/components/views/ProviderAIView"));

// Preload Helper
export const preloadView = (view: string) => {
  switch (view) {
    case "profile": import("@/components/views/ProfileView"); break;
    case "admin-ops": import("@/components/views/AdminOperationsView"); break;
    case "admin-ver": import("@/components/views/AdminVerificationView"); break;
    case "bookings": import("@/components/views/BookingsView"); break;
    case "inbox": import("@/components/views/InboxView"); break;
    case "home": import("@/components/views/HomeView"); break;
    case "hub": import("@/components/views/ProviderHubView"); break;
  }
};

const LazyFallback = () => (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin text-brand-text-muted" size={32} />
  </div>
);

interface ViewRouterProps {
  onToggleRole: () => void;
}

const ViewRouter = memo(({ onToggleRole }: ViewRouterProps) => {
  const {
    currentView,
    selectedProviderId,
    seeAllMode,
    selectedCategory,
    userRole,
    navigateTo,
    goBack,
    handleTabNavigate,
    setSelectedProviderId,
    setSeeAllMode,
    setSelectedCategory,
    setUserRole,
  } = useAppStore(
    useShallow((state) => ({
      currentView: state.currentView,
      selectedProviderId: state.selectedProviderId,
      seeAllMode: state.seeAllMode,
      selectedCategory: state.selectedCategory,
      userRole: state.userRole,
      navigateTo: state.navigateTo,
      goBack: state.goBack,
      handleTabNavigate: state.handleTabNavigate,
      setSelectedProviderId: state.setSelectedProviderId,
      setSeeAllMode: state.setSeeAllMode,
      setSelectedCategory: state.setSelectedCategory,
      setUserRole: state.setUserRole,
    }))
  );

  useEffect(() => {
    // Preload critical views on mount
    const timeout = setTimeout(() => {
      preloadView("profile");
      preloadView("home");
      preloadView("bookings");
      preloadView("inbox");
    }, 4000); // Increased delay to 4s to stay clear of FCP/LCP window
    return () => clearTimeout(timeout);
  }, []);

  const handleSelectProvider = (id: string) => {
    setSelectedProviderId(id);
    navigateTo("provider-profile");
  };

  const handleOpenChat = () => {
    if (selectedProviderId) {
      navigateTo("chat");
    }
  };

  const handleOpenChatFromInbox = (providerId: string) => {
    setSelectedProviderId(providerId);
    navigateTo("chat");
  };

  const handleSeeAll = (mode: "top" | "recommended") => {
    setSeeAllMode(mode);
    navigateTo("all-providers");
  };

  const handleCategorySelect = (cat: string) => {
    setSelectedCategory(cat);
    navigateTo("service-results");
  };

  const renderView = () => {
    if (currentView === "job-history") {
      return <ProviderJobHistoryView onBack={() => goBack("jobs")} />;
    }

    if (currentView === "upcoming-jobs") {
      return <ProviderUpcomingJobsView onBack={() => goBack("jobs")} />;
    }

    if (currentView === "all-providers" && seeAllMode) {
      return (
        <AllProvidersView
          mode={seeAllMode}
          onSelectProvider={handleSelectProvider}
          onBack={() => goBack("home")}
        />
      );
    }

    if (currentView === "service-results" && selectedCategory) {
      return (
        <ServiceResultsView
          category={selectedCategory}
          onSelectProvider={handleSelectProvider}
          onBack={() => goBack("home")}
        />
      );
    }

    if (currentView === "admin-verification") {
      return <AdminVerificationView onBack={() => goBack("profile")} />;
    }

    if (currentView === "admin-operations") {
      return (
        <AdminOperationsView
          onBack={() => goBack("profile")}
          onOpenVerifications={() => navigateTo("admin-verification")}
        />
      );
    }

    if (currentView === "business-onboarding") {
      return (
        <BusinessOnboardingView
          onComplete={() => {
            setUserRole("provider");
            handleTabNavigate("hub");
          }}
          onBack={() => {
            setUserRole("consumer"); // Reset role on cancel
            goBack("profile");
          }}
        />
      );
    }

    if (currentView === "chat" && selectedProviderId) {
      return <ChatView providerId={selectedProviderId} onBack={() => goBack("home")} />;
    }

    if (currentView === "system-notifications") {
      return <ChatView providerId="" onBack={() => goBack("inbox")} isSystemMode />;
    }

    if (currentView === "provider-profile" && selectedProviderId) {
      return (
        <ProviderProfileView
          providerId={selectedProviderId}
          onBack={() => goBack("home")}
          onChat={handleOpenChat}
        />
      );
    }

    switch (currentView) {
      case "home":
        return <HomeView onSelectProvider={handleSelectProvider} onSeeAll={handleSeeAll} onCategorySelect={handleCategorySelect} />;
      case "bookings":
        return <BookingsView />;
      case "inbox":
        return userRole === "provider" ? (
          <ProviderInboxView />
        ) : (
          <InboxView
            onOpenChat={handleOpenChatFromInbox}
            onOpenSystemNotifications={() => navigateTo("system-notifications")}
          />
        );
      case "profile":
        return (
          <ProfileView
            userRole={userRole}
            onToggleRole={onToggleRole}
            onOpenAdmin={() => navigateTo("admin-verification")}
            onOpenAdminOps={() => navigateTo("admin-operations")}
          />
        );
      case "hub":
        return <ProviderHubView onDisableProviderMode={() => { onToggleRole(); handleTabNavigate("profile"); }} />;
      case "jobs":
        return (
          <ProviderJobsView
            onSeeAllHistory={() => navigateTo("job-history")}
            onSeeAllUpcoming={() => navigateTo("upcoming-jobs")}
          />
        );
      case "ai":
        return <ProviderAIView />;
      default:
        return userRole === "provider" ? (
          <ProviderHubView />
        ) : (
          <HomeView onSelectProvider={handleSelectProvider} onSeeAll={handleSeeAll} onCategorySelect={handleCategorySelect} />
        );
    }
  };

  return (
    <Suspense fallback={<LazyFallback />}>
      <div className="flex-1 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-[5px] duration-300 fill-mode-both">
        {renderView()}
      </div>
    </Suspense>
  );
});

ViewRouter.displayName = "ViewRouter";

export default ViewRouter;
